import React, { useEffect } from 'react';
import Dropdown from '../../components/connectedForm/Dropdown';
import BlurTextField from '../../components/connectedForm/BlurTextField';
import { default as Form } from '../../components/connectedForm';
import { useValueState } from '../../utils/hooks';
import { Maybe } from '../../graphql/schema/p4types';
import { IDynamicReportEmailConfig, ILabelValuePair } from '../../actions/useDynamicReportAction';

/**
 * Email Editor component with partial functionality (For extension later).
 *
 * Each field can be enabled/disabled as required (E.g. Acknowledgements do not require setting recipients so it is disabled in that use case).
 *
 * emailSubject: Plain Text
 * emailTo: Array of Email Addresses
 */
const EmailEditor = ({
  allowSubject,
  allowEmailCc,
  allowEmailTo,
  emailToReadOnly,
  emailFrom,
  emailTo: emailToProp,
  emailCc: emailCcProp,
  emailSubject: emailSubjectProp,
  onUpdate,
}: Partial<IDynamicReportEmailConfig> & {
  allowSubject?: boolean;
  allowEmailCc?: boolean;
  allowEmailTo?: boolean;
  emailToReadOnly?: boolean;
  onUpdate: (commit: IDynamicReportEmailConfig) => void;
}) => {
  const [emailTo, setEmailTo] = useValueState<Maybe<Array<ILabelValuePair>>>(emailToProp);
  const [emailCc, setEmailCc] = useValueState<Maybe<Array<ILabelValuePair>>>(emailCcProp);
  const [emailSubject, setEmailSubject] = useValueState(emailSubjectProp);

  useEffect(() => {
    onUpdate({
      emailFrom,
      emailTo: emailTo ?? [],
      emailCc: emailCc ?? [],
      emailSubject,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTo, emailCc, emailSubject, emailFrom]);

  /**
   * Convert a list of emails into a format that the multi-select dropdown can handle.
   * @param emailTo - An array or comma seperated string of email addresses.
   * @returns {*} An array in the format [{label, value}, {label, value}]
   */
  const getEmailList = (emailTo: Maybe<Array<ILabelValuePair>> | string): Array<ILabelValuePair> => {
    if (!emailTo) {
      return [];
    } else if (Array.isArray(emailTo)) {
      return emailTo;
    }
    const tempEmailArray = emailTo.split(',');
    return tempEmailArray.map((email: string) => {
      return { label: email, value: email };
    });
  };

  return (
    <div>
      <Form.Legend label={'Email Configuration'} styles={{ root: { marginRight: 0 } }} />
      {emailFrom ? (
        <BlurTextField label="From" value={emailFrom} readOnly={true} data-testid={'email-editor-from'} />
      ) : null}
      {allowEmailTo ? (
        <Dropdown
          readOnly={emailToReadOnly}
          dataid="emailTo"
          options={[]}
          label={'To'}
          multi={true}
          value={getEmailList(emailTo)}
          onChange={setEmailTo}
          creatable={true}
          rawLabel={true}
        />
      ) : null}
      {allowEmailCc ? (
        <Dropdown
          dataid="emailCc"
          options={[]}
          label={'Cc'}
          multi={true}
          value={getEmailList(emailCc)}
          onChange={setEmailCc}
          creatable={true}
          rawLabel={true}
        />
      ) : null}
      {allowSubject ? (
        <BlurTextField
          label="Subject"
          value={emailSubject}
          onChange={setEmailSubject}
          enableOnChange
          data-testid={'email-editor-subject'}
        />
      ) : null}
    </div>
  );
};

EmailEditor.defaultProps = {
  allowEmailTo: false,
  emailToReadOnly: true,
  allowEmailCc: false,
  allowSubject: true,
  emailTo: [],
  emailCc: [],
  emailSubject: '',
};

export default EmailEditor;
